import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router';
import { DynamicWebRoutes, WebRoutes } from './utils/constants';
import Landing from '@/components/pages/Landing';
import NativeProvider from '@/components/providers/NativeProvider';
import AuthenticationProvider from '@/components/providers/AuthenticationProvider';
import ChakraProvider from '@/components/providers/ChakraProvider';
import ChannelLayout from '@/components/layouts/ChannelLayout';
import Dashboard from '@/components/pages/Dashboard';
import CommandsCustom from '@/components/pages/CommandsCustom';
import CommandsDefault from '@/components/pages/CommandsDefault';
import CommandsPublic from '@/components/pages/CommandsPublic';
import CommandsLayout from '@/components/layouts/CommandsLayout';
import Timers from '@/components/pages/Timers';
import LogsLayout from '@/components/layouts/LogsLayout';
import MessageLogs from '@/components/pages/MessageLogs';
import Regulars from '@/components/pages/Regulars';
import TimeoutLogs from '@/components/pages/TimeoutLogs';
import TwoFactor from '@/components/pages/TwoFactor';
import OAuth2 from '@/components/pages/OAuth2';
import Login from '@/components/pages/Login';
import LoginCallback from '@/components/pages/LoginCallback';
import LoginSSO from '@/components/pages/LoginSSO';
import LoginAuthorized from '@/components/pages/LoginAuthorized';
import AuthenticationRequiredLayout from '@/components/layouts/AuthenticationRequiredLayout';
import IntlProvider from './i18n/IntlProvider';
import Giveaways from './components/pages/Giveaways';
import SpamProtection from './components/pages/SpamProtection';
import PublicChannelLayout from '@/components/layouts/PublicChannelLayout';
import Integrations from './components/pages/Integrations';
import DiscordIntegration from './components/integrations/DiscordIntegration';
import PopupFlowCallback from './components/pages/PopupFlowCallback';
import SongRequests from './components/pages/SongRequests';
import SocketProvider from './components/providers/SocketProvider';
import SongRequestSettings from './components/pages/SongRequestSettings';
import AccountSettings from './components/pages/AccountSettings';
import OAuth2ApplicationSettings from './components/pages/OAuthApplicationSettings';
import { CommandsCustomCreate, CommandsCustomEdit } from './components/pages/CommandsCustomCreate';
import { TimersCreate, TimersEdit } from './components/pages/TimersCreate';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsOfService from './components/pages/TermsOfService';
import SpamProtectionFilter from './components/pages/SpamProtectionFilter';
import SongRequestsPublic from './components/pages/SongRequestsPublic';
import Playlist from './components/pages/Playlist';
import LegalLayout from './components/layouts/LegalLayout';
function PublicChannel() {
    var _useParams = useParams(), provider = _useParams.provider, username = _useParams.username;
    if (typeof provider !== 'string' || typeof username !== 'string') {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: WebRoutes.HOME,
            replace: true
        });
    }
    return /*#__PURE__*/ _jsx(Navigate, {
        to: DynamicWebRoutes.PUBLIC_CHANNEL_COMMANDS(provider !== null && provider !== void 0 ? provider : '', username !== null && username !== void 0 ? username : ''),
        replace: true
    });
}
function PlaylistPublic() {
    var _useParams = useParams(), provider = _useParams.provider, username = _useParams.username;
    if (typeof provider !== 'string' || typeof username !== 'string') {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: WebRoutes.HOME,
            replace: true
        });
    }
    return /*#__PURE__*/ _jsx(Navigate, {
        to: DynamicWebRoutes.PUBLIC_CHANNEL_SONG_REQUESTS(provider !== null && provider !== void 0 ? provider : '', username !== null && username !== void 0 ? username : ''),
        replace: true
    });
}
function LegacyAccountSettings() {
    var path = useParams().panel;
    return /*#__PURE__*/ _jsx(Navigate, {
        to: DynamicWebRoutes.SETTINGS(path),
        replace: true
    });
}
function AppRoutes() {
    return /*#__PURE__*/ _jsxs(Routes, {
        children: [
            /*#__PURE__*/ _jsx(Route, {
                path: WebRoutes.POPUP_FLOW_CALLBACK,
                element: /*#__PURE__*/ _jsx(PopupFlowCallback, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: WebRoutes.LOGIN_CALLBACK,
                element: /*#__PURE__*/ _jsx(LoginCallback, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: WebRoutes.TWO_FACTOR,
                element: /*#__PURE__*/ _jsx(TwoFactor, {})
            }),
            /*#__PURE__*/ _jsxs(Route, {
                element: /*#__PURE__*/ _jsx(AuthenticationProvider, {}),
                children: [
                    /*#__PURE__*/ _jsx(Route, {
                        path: WebRoutes.HOME,
                        element: /*#__PURE__*/ _jsx(Landing, {})
                    }),
                    /*#__PURE__*/ _jsx(Route, {
                        path: WebRoutes.LOGIN,
                        element: /*#__PURE__*/ _jsx(Login, {})
                    }),
                    /*#__PURE__*/ _jsx(Route, {
                        path: WebRoutes.LOGIN_AUTHORIZED,
                        element: /*#__PURE__*/ _jsx(LoginAuthorized, {})
                    }),
                    /*#__PURE__*/ _jsx(Route, {
                        path: WebRoutes.LOGIN_SSO,
                        element: /*#__PURE__*/ _jsx(LoginSSO, {})
                    }),
                    /*#__PURE__*/ _jsxs(Route, {
                        element: /*#__PURE__*/ _jsx(AuthenticationRequiredLayout, {}),
                        children: [
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.OAUTH2_AUTHORIZE,
                                element: /*#__PURE__*/ _jsx(OAuth2, {})
                            }),
                            /*#__PURE__*/ _jsxs(Route, {
                                element: /*#__PURE__*/ _jsx(ChannelLayout, {}),
                                children: [
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: "*",
                                        element: /*#__PURE__*/ _jsx(Navigate, {
                                            to: WebRoutes.DASHBOARD,
                                            replace: true
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.DASHBOARD,
                                        element: /*#__PURE__*/ _jsx(Dashboard, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(Route, {
                                        path: WebRoutes.COMMANDS,
                                        element: /*#__PURE__*/ _jsx(CommandsLayout, {}),
                                        children: [
                                            /*#__PURE__*/ _jsx(Route, {
                                                index: true,
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: WebRoutes.COMMANDS_CUSTOM,
                                                    replace: true
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.COMMANDS_CUSTOM,
                                                element: /*#__PURE__*/ _jsx(CommandsCustom, {})
                                            }),
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.COMMANDS_DEFAULT,
                                                element: /*#__PURE__*/ _jsx(CommandsDefault, {})
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.COMMANDS_CUSTOM_CREATE,
                                        element: /*#__PURE__*/ _jsx(CommandsCustomCreate, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.COMMANDS_CUSTOM_EDIT,
                                        element: /*#__PURE__*/ _jsx(CommandsCustomEdit, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.TIMERS,
                                        element: /*#__PURE__*/ _jsx(Timers, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.TIMERS_EDIT,
                                        element: /*#__PURE__*/ _jsx(TimersEdit, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.TIMERS_CREATE,
                                        element: /*#__PURE__*/ _jsx(TimersCreate, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(Route, {
                                        path: WebRoutes.LOGS,
                                        element: /*#__PURE__*/ _jsx(LogsLayout, {}),
                                        children: [
                                            /*#__PURE__*/ _jsx(Route, {
                                                index: true,
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: WebRoutes.LOGS_MESSAGES,
                                                    replace: true
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.LOGS_MESSAGES,
                                                element: /*#__PURE__*/ _jsx(MessageLogs, {})
                                            }),
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.LOGS_TIMEOUTS,
                                                element: /*#__PURE__*/ _jsx(TimeoutLogs, {})
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.REGULARS,
                                        element: /*#__PURE__*/ _jsx(Regulars, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.INTEGRATIONS,
                                        element: /*#__PURE__*/ _jsx(Integrations, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.INTEGRATIONS_DISCORD,
                                        element: /*#__PURE__*/ _jsx(DiscordIntegration, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.MODERATION,
                                        element: /*#__PURE__*/ _jsx(SpamProtection, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SPAM_PROTECTION_FILTER,
                                        element: /*#__PURE__*/ _jsx(SpamProtectionFilter, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SPAM_PROTECTION,
                                        element: /*#__PURE__*/ _jsx(Navigate, {
                                            to: WebRoutes.MODERATION,
                                            replace: true
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(Route, {
                                        element: /*#__PURE__*/ _jsx(SocketProvider, {}),
                                        children: [
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.GIVEAWAYS,
                                                element: /*#__PURE__*/ _jsx(Giveaways, {})
                                            }),
                                            /*#__PURE__*/ _jsx(Route, {
                                                path: WebRoutes.SONG_REQUESTS,
                                                element: /*#__PURE__*/ _jsx(SongRequests, {})
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SONG_REQUESTS_PLAYLIST,
                                        element: /*#__PURE__*/ _jsx(Playlist, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SONG_REQUESTS_SETTINGS,
                                        element: /*#__PURE__*/ _jsx(SongRequestSettings, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SETTINGS_PANEL,
                                        element: /*#__PURE__*/ _jsx(AccountSettings, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.SETTINGS,
                                        element: /*#__PURE__*/ _jsx(AccountSettings, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.ACCOUNT,
                                        element: /*#__PURE__*/ _jsx(Navigate, {
                                            to: WebRoutes.SETTINGS
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.ACCOUNT_PANEL,
                                        element: /*#__PURE__*/ _jsx(LegacyAccountSettings, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Route, {
                                        path: WebRoutes.OAUTH2_APPLICATION_SETTINGS,
                                        element: /*#__PURE__*/ _jsx(OAuth2ApplicationSettings, {})
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Route, {
                        element: /*#__PURE__*/ _jsx(LegalLayout, {}),
                        children: [
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.PRIVACY,
                                element: /*#__PURE__*/ _jsx(PrivacyPolicy, {})
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.TERMS,
                                element: /*#__PURE__*/ _jsx(TermsOfService, {})
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Route, {
                        path: WebRoutes.PUBLIC_CHANNEL,
                        element: /*#__PURE__*/ _jsx(PublicChannelLayout, {}),
                        children: [
                            /*#__PURE__*/ _jsx(Route, {
                                index: true,
                                element: /*#__PURE__*/ _jsx(PublicChannel, {})
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.PUBLIC_CHANNEL_COMMANDS,
                                element: /*#__PURE__*/ _jsx(CommandsPublic, {})
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.PUBLIC_CHANNEL_SONG_REQUESTS,
                                element: /*#__PURE__*/ _jsx(SongRequestsPublic, {})
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: WebRoutes.PUBLIC_CHANNEL_SONG_REQUESTS_PLAYLIST,
                                element: /*#__PURE__*/ _jsx(PlaylistPublic, {})
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function ScrollToHash() {
    var hash = useLocation().hash;
    React.useEffect(function() {
        if (hash == null || hash.length === 0) {
            return;
        }
        var element = document.querySelector(hash);
        if (element == null) {
            return;
        }
        element.scrollIntoView({
            behavior: 'smooth'
        });
    }, [
        hash
    ]);
    return null;
}
function App() {
    return /*#__PURE__*/ _jsxs(BrowserRouter, {
        children: [
            /*#__PURE__*/ _jsx(ScrollToHash, {}),
            /*#__PURE__*/ _jsx(NativeProvider, {
                children: /*#__PURE__*/ _jsx(IntlProvider, {
                    children: /*#__PURE__*/ _jsx(ChakraProvider, {
                        children: /*#__PURE__*/ _jsx(AppRoutes, {})
                    })
                })
            })
        ]
    });
}
var rootElement = document.getElementById('root');
if (rootElement === null) {
    throw new Error('Root element not found');
}
var root = createRoot(rootElement);
root.render(/*#__PURE__*/ _jsx(App, {}));
